<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div>
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Billing & Revenue</h1>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Platform-wide billing overview and subscription metrics
      </p>
    </div>

    <!-- Revenue Statistics -->
    <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <!-- Total Revenue -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <CurrencyDollarIcon class="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Total Revenue
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  ${{ formatNumber(stats.revenue.total) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- This Month Revenue -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ChartBarIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  This Month
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  ${{ formatNumber(stats.revenue.this_month) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- MRR (Monthly Recurring Revenue) -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <ArrowTrendingUpIcon class="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  MRR
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  ${{ formatNumber(stats.mrr) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <!-- ARR (Annual Recurring Revenue) -->
      <div class="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <CalendarIcon class="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  ARR
                </dt>
                <dd class="text-lg font-semibold text-gray-900 dark:text-white">
                  ${{ formatNumber(stats.arr) }}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Methods & Status Breakdown -->
    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <!-- Payment Methods Breakdown -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white mb-4">
            Payment Methods
          </h3>
          <div class="space-y-3">
            <div
              v-for="method in paymentMethodStats"
              :key="method.method"
              class="flex items-center justify-between"
            >
              <div class="flex items-center">
                <CreditCardIcon class="h-5 w-5 text-gray-400 mr-3" />
                <span class="text-sm text-gray-700 dark:text-gray-300">{{ method.method }}</span>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ method.count }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400 w-12 text-right">
                  {{ method.percentage }}%
                </span>
                <div class="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    class="bg-blue-600 h-2 rounded-full"
                    :style="{ width: method.percentage + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Subscription Status Breakdown -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="px-4 py-5 sm:p-6">
          <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white mb-4">
            Subscription Status
          </h3>
          <div class="space-y-3">
            <div
              v-for="status in statusBreakdown"
              :key="status.status"
              class="flex items-center justify-between"
            >
              <div class="flex items-center">
                <span
                  :class="getStatusIconClass(status.status)"
                  class="inline-block h-3 w-3 rounded-full mr-3"
                ></span>
                <span class="text-sm text-gray-700 dark:text-gray-300">{{ status.status }}</span>
              </div>
              <span class="text-sm font-medium text-gray-900 dark:text-white">
                {{ status.count }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Subscriptions -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-base font-semibold leading-6 text-gray-900 dark:text-white mb-4">
          Recent Subscriptions
        </h3>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Company
                </th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Payment Method
                </th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" class="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Trial Ends
                </th>
                <th scope="col" class="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="subscription in recentSubscriptions"
                :key="subscription.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td class="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {{ subscription.name }}
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ formatPaymentMethod(subscription.payment_method) }}
                </td>
                <td class="px-3 py-4 whitespace-nowrap">
                  <span
                    :class="getStatusBadgeClass(subscription.status)"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    {{ formatStatus(subscription.status) }}
                  </span>
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ formatDate(subscription.created_at) }}
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {{ subscription.trial_ends_at ? formatDate(subscription.trial_ends_at) : 'N/A' }}
                </td>
                <td class="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Link
                    :href="route('site-owner.companies.show', subscription.id)"
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                  >
                    View
                  </Link>
                </td>
              </tr>
              <tr v-if="recentSubscriptions.length === 0">
                <td colspan="6" class="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No subscriptions found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  CreditCardIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  stats: {
    type: Object,
    required: true,
  },
  paymentMethodStats: {
    type: Array,
    required: true,
  },
  recentSubscriptions: {
    type: Array,
    required: true,
  },
  statusBreakdown: {
    type: Array,
    required: true,
  },
});

// Format number with commas
const formatNumber = (number) => {
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Format payment method
const formatPaymentMethod = (method) => {
  const methods = {
    stripe: 'Stripe',
    bank_transfer: 'Bank Transfer',
    free: 'Free',
    zero_plan: 'Zero Plan',
  };
  return methods[method] || method;
};

// Format status
const formatStatus = (status) => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

// Get status badge class
const getStatusBadgeClass = (status) => {
  const classes = {
    active: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    trial: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    suspended: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  };
  return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

// Get status icon class
const getStatusIconClass = (status) => {
  const classes = {
    Active: 'bg-green-500',
    Trial: 'bg-yellow-500',
    Suspended: 'bg-red-500',
    Cancelled: 'bg-gray-500',
  };
  return classes[status] || 'bg-gray-500';
};
</script>
